/**
 * Neureus RAG (Retrieval-Augmented Generation) SDK Client
 *
 * Provides a simple interface to build and query RAG pipelines on the
 * Neureus platform, handling document ingestion, chunking, embedding,
 * retrieval, and answer generation automatically.
 *
 * @example
 * ```typescript
 * import { RAGClient } from '@neureus/sdk/rag';
 *
 * const rag = new RAGClient({
 *   apiKey: 'nru_...',
 *   baseUrl: 'https://api.neureus.ai'
 * });
 *
 * // Create a RAG pipeline
 * await rag.pipelines.create({
 *   name: 'product-docs',
 *   embedding: {
 *     model: 'text-embedding-ada-002',
 *     provider: 'openai'
 *   },
 *   generation: {
 *     model: 'gpt-4',
 *     provider: 'openai'
 *   }
 * });
 *
 * // Ingest documents
 * await rag.ingest('product-docs', {
 *   source: './docs',
 *   type: 'file',
 *   format: 'markdown',
 *   recursive: true
 * });
 *
 * // Query the pipeline
 * const response = await rag.query('product-docs', {
 *   query: 'How do I authenticate users?',
 *   topK: 5
 * });
 *
 * console.log(response.answer);
 * console.log(response.sources);
 * ```
 */

import ky, { type KyInstance } from 'ky';
import type {
  RAGConfig,
  IngestionRequest,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  RAGPipelineInfo,
  ProcessingResult,
  BatchProcessingResult,
} from '@neureus/rag';

// Re-export types from rag for convenience
export type {
  RAGConfig,
  IngestionRequest,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  RAGPipelineInfo,
  RAGPipelineStatus,
  ProcessingResult,
  BatchProcessingResult,
  Document,
  DocumentMetadata,
  DocumentFormat,
  DocumentSource,
  Chunk,
  ChunkMetadata,
  ChunkingConfig,
  ChunkingStrategy,
  EmbeddingConfig,
  ContextResult,
  RAGError,
  DocumentProcessingError,
  EmbeddingError,
  RetrievalError,
  GenerationError,
} from '@neureus/rag';

/**
 * Configuration options for RAGClient
 */
export interface RAGClientConfig {
  /**
   * Neureus API key (required)
   * Get your API key from https://app.neureus.ai/settings/api-keys
   */
  apiKey: string;

  /**
   * Base URL for the Neureus API
   * @default 'https://api.neureus.ai'
   */
  baseUrl?: string;

  /**
   * Request timeout in milliseconds
   * @default 60000 (60 seconds)
   */
  timeout?: number;

  /**
   * Number of retry attempts for failed requests
   * @default 3
   */
  retries?: number;

  /**
   * User ID for usage tracking (optional)
   * @default ''
   */
  userId?: string;

  /**
   * Team ID for usage tracking (optional)
   * @default ''
   */
  teamId?: string;
}

/**
 * Options for document ingestion
 */
export interface IngestOptions extends IngestionRequest {
  /**
   * Wait for ingestion to complete before returning
   * @default false
   */
  waitForCompletion?: boolean;
}

/**
 * Options for RAG queries
 */
export interface QueryOptions extends Omit<QueryRequest, 'streaming'> {
  /**
   * Enable streaming responses
   * @default false
   */
  streaming?: boolean;
}

/**
 * Main RAG client class
 */
export class RAGClient {
  private http: KyInstance;
  private config: Required<RAGClientConfig>;

  constructor(config: RAGClientConfig) {
    this.config = {
      apiKey: config.apiKey,
      baseUrl: config.baseUrl || 'https://api.neureus.ai',
      timeout: config.timeout || 60000,
      retries: config.retries || 3,
      userId: config.userId || '',
      teamId: config.teamId || '',
    };

    this.http = ky.create({
      prefixUrl: this.config.baseUrl,
      timeout: this.config.timeout,
      retry: {
        limit: this.config.retries,
        methods: ['get', 'post'],
        statusCodes: [408, 413, 429, 500, 502, 503, 504],
      },
      hooks: {
        beforeRequest: [
          (request) => {
            request.headers.set('Authorization', `Bearer ${this.config.apiKey}`);
            request.headers.set('Content-Type', 'application/json');
            request.headers.set('User-Agent', 'Neureus-SDK/0.2.0');
          },
        ],
      },
    });
  }

  /**
   * Pipeline management API
   */
  public pipelines = {
    /**
     * Create a new RAG pipeline
     *
     * @example
     * ```typescript
     * await rag.pipelines.create({
     *   name: 'customer-support',
     *   description: 'Customer support knowledge base',
     *   embedding: {
     *     model: 'text-embedding-ada-002',
     *     provider: 'openai',
     *     dimensions: 1536
     *   },
     *   chunking: {
     *     strategy: 'recursive',
     *     size: 512,
     *     overlap: 128
     *   },
     *   generation: {
     *     model: 'gpt-4',
     *     provider: 'openai',
     *     temperature: 0.1
     *   }
     * });
     * ```
     */
    create: async (config: RAGConfig): Promise<{ success: boolean; pipeline: RAGPipelineInfo }> => {
      return this.http.post('rag/pipelines', {
        json: config,
      }).json();
    },

    /**
     * List all RAG pipelines
     *
     * @example
     * ```typescript
     * const pipelines = await rag.pipelines.list();
     * console.log(pipelines); // [{ name: 'docs', status: 'ready', ... }]
     * ```
     */
    list: async (): Promise<{ pipelines: RAGPipelineInfo[]; count: number }> => {
      return this.http.get('rag/pipelines').json();
    },

    /**
     * Get pipeline information
     *
     * @example
     * ```typescript
     * const info = await rag.pipelines.get('product-docs');
     * console.log(info.stats.documentsCount, info.stats.totalQueries);
     * ```
     */
    get: async (pipelineName: string): Promise<RAGPipelineInfo> => {
      return this.http.get(`rag/pipelines/${pipelineName}`).json();
    },

    /**
     * Update pipeline configuration
     *
     * @example
     * ```typescript
     * await rag.pipelines.update('product-docs', {
     *   generation: {
     *     temperature: 0.5
     *   }
     * });
     * ```
     */
    update: async (
      pipelineName: string,
      updates: Partial<RAGConfig>
    ): Promise<{ success: boolean; pipeline: RAGPipelineInfo }> => {
      return this.http.patch(`rag/pipelines/${pipelineName}`, {
        json: updates,
      }).json();
    },

    /**
     * Delete a pipeline
     *
     * @example
     * ```typescript
     * await rag.pipelines.delete('old-docs');
     * ```
     */
    delete: async (pipelineName: string): Promise<{ success: boolean; message: string }> => {
      return this.http.delete(`rag/pipelines/${pipelineName}`).json();
    },
  };

  /**
   * Ingest documents into a RAG pipeline
   *
   * @example
   * ```typescript
   * // Ingest from file
   * await rag.ingest('product-docs', {
   *   source: './docs',
   *   type: 'file',
   *   format: 'markdown',
   *   recursive: true
   * });
   *
   * // Ingest from URL
   * await rag.ingest('product-docs', {
   *   source: 'https://example.com/docs',
   *   type: 'url',
   *   format: 'html'
   * });
   *
   * // Ingest text directly
   * await rag.ingest('product-docs', {
   *   source: 'This is my document content...',
   *   type: 'text',
   *   metadata: { title: 'Introduction' }
   * });
   * ```
   */
  async ingest(
    pipelineName: string,
    options: IngestOptions
  ): Promise<ProcessingResult | BatchProcessingResult> {
    const { waitForCompletion, ...ingestionRequest } = options;

    if (waitForCompletion) {
      return this.http.post(`rag/pipelines/${pipelineName}/ingest`, {
        json: ingestionRequest,
        searchParams: { wait: 'true' },
      }).json();
    }

    return this.http.post(`rag/pipelines/${pipelineName}/ingest`, {
      json: ingestionRequest,
    }).json();
  }

  /**
   * Query a RAG pipeline (non-streaming)
   *
   * @example
   * ```typescript
   * const response = await rag.query('product-docs', {
   *   query: 'How do I authenticate users?',
   *   topK: 5,
   *   minSimilarity: 0.7,
   *   includeSource: true
   * });
   *
   * console.log('Answer:', response.answer);
   * console.log('Sources:', response.sources);
   * console.log('Performance:', response.performance);
   * ```
   */
  async query(pipelineName: string, options: QueryOptions): Promise<QueryResponse> {
    const { streaming, ...queryRequest } = options;

    return this.http.post(`rag/pipelines/${pipelineName}/query`, {
      json: {
        ...queryRequest,
        streaming: false,
        userId: this.config.userId || undefined,
        teamId: this.config.teamId || undefined,
      },
    }).json();
  }

  /**
   * Query a RAG pipeline with streaming response
   *
   * @example
   * ```typescript
   * const stream = await rag.queryStream('product-docs', {
   *   query: 'Explain the authentication flow',
   *   topK: 5
   * });
   *
   * for await (const chunk of stream) {
   *   if (chunk.type === 'answer') {
   *     process.stdout.write(chunk.data.content);
   *   } else if (chunk.type === 'complete') {
   *     console.log('\nSources:', chunk.data.sources);
   *   }
   * }
   * ```
   */
  async queryStream(
    pipelineName: string,
    options: QueryOptions
  ): Promise<AsyncIterable<QueryStreamChunk>> {
    const { streaming: _, ...queryRequest } = options;

    const response = await this.http.post(`rag/pipelines/${pipelineName}/query`, {
      json: {
        ...queryRequest,
        streaming: true,
        userId: this.config.userId || undefined,
        teamId: this.config.teamId || undefined,
      },
    });

    return this.parseSSEStream(response.body!);
  }

  /**
   * Get pipeline statistics
   *
   * @example
   * ```typescript
   * const stats = await rag.stats('product-docs');
   * console.log(`Documents: ${stats.documentsCount}`);
   * console.log(`Queries: ${stats.totalQueries}`);
   * console.log(`Avg response time: ${stats.avgResponseTime}ms`);
   * ```
   */
  async stats(pipelineName: string): Promise<RAGPipelineInfo['stats']> {
    const info = await this.pipelines.get(pipelineName);
    return info.stats;
  }

  /**
   * Clear all documents from a pipeline
   *
   * @example
   * ```typescript
   * await rag.clear('product-docs');
   * ```
   */
  async clear(pipelineName: string): Promise<{ success: boolean; message: string }> {
    return this.http.delete(`rag/pipelines/${pipelineName}/documents`).json();
  }

  /**
   * Parse Server-Sent Events stream into async iterable
   */
  private async *parseSSEStream(body: ReadableStream<Uint8Array>): AsyncIterable<QueryStreamChunk> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();

          if (trimmed === '') continue;
          if (trimmed.startsWith(':')) continue; // Comment
          if (trimmed === 'data: [DONE]') return;

          if (trimmed.startsWith('data: ')) {
            const data = trimmed.slice(6);
            try {
              const chunk = JSON.parse(data) as QueryStreamChunk;
              yield chunk;
            } catch (error) {
              console.error('Failed to parse SSE data:', data, error);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

/**
 * Create a RAG client instance
 *
 * @example
 * ```typescript
 * import { createRAGClient } from '@neureus/sdk/rag';
 *
 * const rag = createRAGClient({
 *   apiKey: process.env.NEUREUS_API_KEY
 * });
 * ```
 */
export function createRAGClient(config: RAGClientConfig): RAGClient {
  return new RAGClient(config);
}
